import Quill from 'quill';

type TQuillWithTooltip = Quill & {
  theme?: {
    tooltip?: {
      root: HTMLElement;
      textbox: HTMLInputElement;
      edit: (mode?: string, preview?: string) => void;
    };
  };
  selection?: { savedRange?: { index: number; length: number } };
  container: HTMLElement;
};

const CONTAINER_MARGIN_PX = 8;

/**
 * Stops Quill's link tooltip from scrolling the page when it opens.
 */
export function preventLinkTooltipFocusScroll(quill: Quill): void {
  const tooltip = (quill as TQuillWithTooltip).theme?.tooltip;
  if (!tooltip) return;

  const originalSelect = tooltip.textbox.select.bind(tooltip.textbox);
  tooltip.textbox.select = (): void => {
    tooltip.textbox.focus({ preventScroll: true });
    originalSelect();
  };
}

/**
 * Pins the link tooltip's bottom 8 px above the editor container's bottom,
 * with horizontal alignment to the user's selection.
 */
export function keepLinkTooltipInsideContainer(quill: Quill): void {
  const internals = quill as TQuillWithTooltip;
  const tooltip = internals.theme?.tooltip;
  if (!tooltip) return;

  const originalEdit = tooltip.edit.bind(tooltip);
  tooltip.edit = (mode?: string, preview?: string): void => {
    originalEdit(mode, preview);

    const savedRange = internals.selection?.savedRange;
    if (!savedRange) return;
    const bounds = quill.getBounds(savedRange.index, savedRange.length);
    if (!bounds) return;

    const containerRect = internals.container.getBoundingClientRect();
    const maxLeft = containerRect.width - tooltip.root.offsetWidth - CONTAINER_MARGIN_PX;
    const rawLeft = bounds.left - containerRect.left;
    const left = Math.max(CONTAINER_MARGIN_PX, Math.min(rawLeft, maxLeft));

    tooltip.root.style.setProperty('bottom', `${CONTAINER_MARGIN_PX}px`, 'important');
    tooltip.root.style.setProperty('left', `${left}px`, 'important');
    tooltip.root.classList.remove('ql-flip');
  };
}
